import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import {
  Link,
  withRouter,
} from 'react-router-dom';
import {
  Button,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Nav,
  NavItem,
  NavLink,
  UncontrolledTooltip,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import { logout } from '../actions/Auth';

import Avatar from './Avatar';
import ViewModeSwitch from './ViewModeSwitch';

import {
  getShare,
  userCan,
  userIsAdmin,
} from '../helpers/Auth';

const ProfileMenu = ({
  history,
  location,
  profile,
  logoutAction,
  toggleNavigation,
  inAdminPanel,
}) => {
  const intl = useIntl();
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const toggle = () => {
    if (dropdownOpen) {
      toggleNavigation();
    }
    setDropdownOpen(prevState => !prevState);
  };

  if (!profile || !profile.uuid) {
    const loginUrl = {
      pathname: '/login',
      search: location.search,
      state: {
        from: location.pathname,
      },
    };

    return (
      <li className="nav-profile dropdown nav-item">
        <Link to={loginUrl} className="nav-link">
          <i className="fas fa-user" />
        </Link>
      </li>
    );
  }

  return (
    <Dropdown
      nav
      direction={getShare() ? 'down' : 'right'}
      className={classnames('nav-profile', { active: dropdownOpen })}
      isOpen={dropdownOpen}
      toggle={toggle}
    >
      <DropdownToggle nav>
        <span className="nav-item-icon">
          {profile && profile.avatar_url ? (
            <img src={profile.avatar_url} alt={profile.name} className="avatar avatar-40" />
          ) : (
            <i className="fas fa-user" />
          )}
        </span>
        <span className="nav-item-title">
          {intl.formatMessage({ defaultMessage: 'My Settings' })}
        </span>
      </DropdownToggle>
      <DropdownMenu
        className={classnames('profile-dropdown', { 'in-admin-panel': inAdminPanel })}
        right
        container="body"
      >
        <div className="profile-wrapper">
          <div className="profile-avatar">
            <DropdownItem
              tag={Link}
              to={`/profile/${profile.uuid}`}
              title={intl.formatMessage({ defaultMessage: 'View Profile' })}
              className="profile-view"
              onClick={toggleNavigation}
            >
              <Avatar user={profile} size={50} />
            </DropdownItem>
          </div>
          <div className="profile-details">
            <DropdownItem
              tag={Link}
              to={`/profile/${profile.uuid}`}
              title={intl.formatMessage({ defaultMessage: 'View Profile' })}
              className="profile-view"
              onClick={toggleNavigation}
            >
              <h6 className="profile-name">
                {profile.name}
              </h6>
              <span className="profile-email">
                {profile.email}
              </span>
            </DropdownItem>
          </div>
          <div className="profile-tools">
            {(profile && profile.uuid) ? (
              <>
                <ViewModeSwitch />
                <DropdownItem
                  tag={Link}
                  to="/profile/edit"
                  title={intl.formatMessage({ defaultMessage: 'Edit Profile' })}
                  id="profile-edit-profile"
                  className="profile-edit"
                  onClick={toggleNavigation}
                >
                  <i className="far fa-pen" />
                </DropdownItem>
                <UncontrolledTooltip fade={false} placement="bottom" target="profile-edit-profile">
                  {intl.formatMessage({ defaultMessage: 'Edit Profile' })}
                </UncontrolledTooltip>
                <DropdownItem
                  tag={Button}
                  onClick={logoutAction}
                  title={intl.formatMessage({ defaultMessage: 'Log Out' })}
                  id="profile-logout"
                  className="profile-logout"
                >
                  <i className="far fa-sign-out" />
                </DropdownItem>
                <UncontrolledTooltip fade={false} placement="bottom" target="profile-logout">
                  {intl.formatMessage({ defaultMessage: 'Log Out' })}
                </UncontrolledTooltip>
              </>
            ) : (
              <Button
                onClick={() => {
                  history.push('/login');
                }}
                title={intl.formatMessage({ defaultMessage: 'Login' })}
                className="profile-login"
              >
                <i className="far fa-sign-in" />
              </Button>
            )}
          </div>
        </div>
        {(userCan('manage_settings') || userCan('add_users')) && (
          <div className="settings-wrapper settings-links-wrapper">
            <header className="settings-header">
              <h6>{intl.formatMessage({ defaultMessage: 'Settings' })}</h6>
              {(userIsAdmin() && !profile.client.payment_disabled) && (
                <Link to="/admin/settings/subscription" onClick={toggle}>
                    {intl.formatMessage({ defaultMessage: 'Subscription' })}
                </Link>
              )}
            </header>
            <Nav className="ml-auto nav-main settings-list" vertical>
              {userCan('manage_settings') && (
                <NavItem>
                  <NavLink tag={Link} to="/admin/settings/teams" onClick={toggle}>
                    <i className="far fa-users-class" />
                    <div className="settings-description">
                      <h5>{intl.formatMessage({ defaultMessage: 'Teams' })}</h5>
                      <p>
                        {intl.formatMessage({ defaultMessage: 'Manage groups of users' })}
                      </p>
                    </div>
                  </NavLink>
                </NavItem>
              )}
              <NavItem>
                <NavLink tag={Link} to="/admin/settings/users" onClick={toggle}>
                  <i className="far fa-users" />
                  <div className="settings-description">
                    <h5>{intl.formatMessage({ defaultMessage: 'Users' })}</h5>
                    <p>
                      {intl.formatMessage({ defaultMessage: 'Manage your users' })}
                    </p>
                  </div>
                </NavLink>
              </NavItem>
              {userCan('manage_settings') && (
                <>
                  <NavItem>
                    <NavLink tag={Link} to="/admin/settings" onClick={toggle}>
                      <i className="far fa-cogs" />
                      <div className="settings-description">
                        <h5>{intl.formatMessage({ defaultMessage: 'Settings' })}</h5>
                        <p>
                          {intl.formatMessage({ defaultMessage: 'Adjust settings' })}
                        </p>
                      </div>
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink tag={Link} to="/admin/reports" onClick={toggle}>
                      <i className="far fa-file-chart-pie" />
                      <div className="settings-description">
                        <h5>{intl.formatMessage({ defaultMessage: 'Reports' })}</h5>
                        <p>
                          {intl.formatMessage({ defaultMessage: 'Generate reports' })}
                        </p>
                      </div>
                    </NavLink>
                  </NavItem>
                </>
              )}
            </Nav>
          </div>
        )}
      </DropdownMenu>
    </Dropdown>
  );
};

const mapDispatchToProps = dispatch => ({
  logoutAction: () => {
    dispatch(logout());
  },
});

const mapStateToProps = state => ({
  profile: state.Auth.profile,
});

ProfileMenu.propTypes = {
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  profile: PropTypes.object,
  logoutAction: PropTypes.func.isRequired,
  toggleNavigation: PropTypes.func,
  inAdminPanel: PropTypes.bool,
};

ProfileMenu.defaultProps = {
  profile: null,
  toggleNavigation: () => {},
  inAdminPanel: false,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ProfileMenu));
